import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import SiteFooter from "@/components/SiteFooter";
import { BUSINESS } from "@/config/business";
import {
  Shield,
  Phone,
  Check,
  Users,
  Clock,
  CheckCircle2,
  Plus,
  Minus,
  ArrowRight,
  ArrowLeft,
  Quote,
} from "lucide-react";

const PHONE_DISPLAY = "(332) 230-1915";
const PHONE_HREF = "tel:+13322301915";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const JOTFORM_ID = "253018379587065";
const JOTFORM_URL = `https://form.jotform.com/${JOTFORM_ID}`;

const scrollToForm = () => {
  const el = document.getElementById("assessment");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Reveal = ({ children, className = "", i = 0 }) => (
  <motion.div
    className={className}
    variants={fadeUp}
    custom={i}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, amount: 0.25 }}
  >
    {children}
  </motion.div>
);

const GoldButton = ({ children, onClick, className = "", testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`group inline-flex items-center justify-center gap-2 rounded-full bg-[#bf9a4d] px-7 py-3.5 text-[15px] font-semibold text-[#1c3325] shadow-[0_10px_30px_-12px_rgba(191,154,77,0.9)] transition-[background-color,transform,box-shadow] duration-300 hover:bg-[#a9853c] hover:-translate-y-0.5 active:translate-y-0 ${className}`}
  >
    {children}
  </button>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "border-[#e2ddcf] bg-[#f4f1e9]/90 backdrop-blur-md"
          : "border-transparent bg-[#f4f1e9]/60 backdrop-blur-sm"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <a
          href="#top"
          data-testid="logo-link"
          className="flex items-center gap-2.5"
        >
          <Shield className="h-7 w-7 text-[#223f2e]" strokeWidth={2} />
          <span className="font-display text-[22px] font-600 font-semibold tracking-tight text-[#223f2e]">
            Guardian Advisory
          </span>
        </a>
        <div className="flex items-center gap-5 sm:gap-8">
          <a
            href="#savings"
            data-testid="nav-savings-calculator"
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById("savings");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="hidden items-center text-[15px] font-semibold text-[#223f2e] transition-opacity hover:opacity-70 md:flex"
          >
            Savings Calculator
          </a>
          <a
            href={PHONE_HREF}
            data-testid="header-phone"
            className="hidden items-center gap-2 text-[15px] font-semibold text-[#223f2e] transition-opacity hover:opacity-70 sm:flex"
          >
            <Phone className="h-4 w-4" strokeWidth={2.2} />
            {PHONE_DISPLAY}
          </a>
          <GoldButton
            testid="header-cta"
            onClick={scrollToForm}
            className="px-6 py-3 text-[14px]"
          >
            Free Assessment
          </GoldButton>
        </div>
      </div>
    </header>
  );
};

const WaveGraphic = () => (
  <svg
    viewBox="0 0 520 90"
    fill="none"
    className="h-auto w-full max-w-[520px]"
    aria-hidden="true"
  >
    <path
      d="M2 55 C 70 30, 120 30, 175 50 C 230 70, 280 72, 340 58 L 518 58"
      stroke="#223f2e"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
    <path
      d="M2 40 C 80 12, 150 78, 230 45 C 300 16, 360 44, 430 40"
      stroke="#bf9a4d"
      strokeWidth="2.5"
      strokeLinecap="round"
      opacity="0.85"
    />
    <path
      d="M2 62 C 90 88, 150 20, 240 58 C 320 90, 370 30, 430 52"
      stroke="#c48b7c"
      strokeWidth="2.5"
      strokeLinecap="round"
      opacity="0.85"
    />
  </svg>
);

const HERO_PHOTOS = [
  "https://images.unsplash.com/photo-1717251816735-62bd01da1757?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
  "https://images.unsplash.com/photo-1753351052363-53ce102830eb?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
  "https://images.unsplash.com/photo-1484863137850-59afcfe05386?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
];

const HeroPhotos = () => (
  <div>
    <div className="flex gap-3">
      {HERO_PHOTOS.map((src, i) => (
        <img
          key={i}
          src={src}
          alt="Business owner"
          loading="lazy"
          className={`h-28 w-24 rounded-2xl border-4 border-white object-cover shadow-[0_20px_40px_-25px_rgba(34,63,46,0.6)] sm:h-32 sm:w-28 ${
            i === 1 ? "mt-5" : ""
          }`}
        />
      ))}
    </div>
    <p className="mt-4 font-mono-label text-[11px] text-[#4f7a5e]">
      Helping business owners across the U.S.
    </p>
  </div>
);

const DEBT_RANGES = [
  "Under $50,000",
  "$50,000 – $100,000",
  "$100,000 – $250,000",
  "$250,000 – $500,000",
  "$500,000+",
];
const POSITION_OPTIONS = ["1", "2", "3", "4", "5 or more"];

const fieldBase =
  "mt-1.5 w-full rounded-xl border border-[#e0dccd] bg-[#faf8f2] px-4 py-3 text-[15px] text-[#1e3a2a] outline-none transition-colors placeholder:text-[#a8ab9f] focus:border-[#bf9a4d] focus:bg-white";
const labelBase = "font-mono-label text-[11px] text-[#6b7268]";

const AssessmentForm = () => {
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    business_name: "",
    total_debt: "",
    active_positions: "",
    consent: false,
  });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [error, setError] = useState("");

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consent) {
      setError("Please agree to be contacted before submitting.");
      return;
    }
    setStatus("submitting");
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      setStatus("success");
      if (typeof window.gtag_report_conversion === "function") {
        window.gtag_report_conversion();
      }
    } catch (err) {
      setStatus("error");
      setError("Something went wrong. Please try again or call us directly.");
    }
  };

  return (
    <div
      id="assessment"
      data-testid="assessment-card"
      className="scroll-mt-28 rounded-[26px] border border-[#ece7d9] bg-white p-6 shadow-[0_40px_80px_-40px_rgba(34,63,46,0.35)] sm:p-8"
    >
      <h2 className="font-display text-[26px] font-semibold leading-tight text-[#1e3a2a] sm:text-[30px]">
        Get Your Free Debt Assessment
      </h2>
      <p className="mt-2 text-[15px] leading-relaxed text-[#566257]">
        Tell us a bit about your situation — a consolidation specialist will
        call you within 24 hours.
      </p>

      {status === "success" ? (
        <div
          data-testid="assessment-success"
          className="mt-8 rounded-2xl bg-[#e7efe6] p-8 text-center"
        >
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#223f2e]">
            <Check className="h-7 w-7 text-[#f4f1e9]" strokeWidth={3} />
          </div>
          <h3 className="mt-5 font-display text-[24px] font-semibold text-[#1e3a2a]">
            Request received
          </h3>
          <p className="mx-auto mt-3 max-w-sm text-[15px] leading-relaxed text-[#4b5a4f]">
            Thanks, {form.full_name.split(" ")[0] || "there"}. A consolidation
            specialist will reach out within 24 hours.
          </p>
          <div className="mt-6">
            <p className="text-[14px] font-medium text-[#25402f]">
              Want to skip the wait?
            </p>
            <a
              href={BUSINESS.calendlyUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="success-calendly-btn"
              className="group mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-[#223f2e] px-7 py-3.5 text-[15px] font-semibold text-[#f4f1e9] transition-[background-color,transform] duration-300 hover:bg-[#1c3325] hover:-translate-y-0.5"
            >
              Book Your Call Now
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <p className="mt-4 text-[13px] text-[#566257]">
              Or call us at{" "}
              <a href={PHONE_HREF} className="font-semibold text-[#1e3a2a] underline">
                {PHONE_DISPLAY}
              </a>
            </p>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-6 space-y-5" noValidate>
          <div>
            <label className={labelBase} htmlFor="full_name">
              Full Name
            </label>
            <input
              id="full_name"
              data-testid="input-full-name"
              type="text"
              required
              value={form.full_name}
              onChange={(e) => update("full_name", e.target.value)}
              className={fieldBase}
              placeholder="Jane Smith"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelBase} htmlFor="phone">
                Phone
              </label>
              <input
                id="phone"
                data-testid="input-phone"
                type="tel"
                required
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                className={fieldBase}
                placeholder="(555) 123-4567"
              />
            </div>
            <div>
              <label className={labelBase} htmlFor="email">
                Email
              </label>
              <input
                id="email"
                data-testid="input-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className={fieldBase}
                placeholder="you@business.com"
              />
            </div>
          </div>

          <div>
            <label className={labelBase} htmlFor="business_name">
              Business Name
            </label>
            <input
              id="business_name"
              data-testid="input-business-name"
              type="text"
              required
              value={form.business_name}
              onChange={(e) => update("business_name", e.target.value)}
              className={fieldBase}
              placeholder="Smith & Co."
            />
          </div>

          <div>
            <label className={labelBase} htmlFor="total_debt">
              Total MCA Debt
            </label>
            <select
              id="total_debt"
              data-testid="select-total-debt"
              required
              value={form.total_debt}
              onChange={(e) => update("total_debt", e.target.value)}
              className={`${fieldBase} ${!form.total_debt ? "text-[#a8ab9f]" : ""}`}
            >
              <option value="" disabled>
                Select a range
              </option>
              {DEBT_RANGES.map((r) => (
                <option key={r} value={r} className="text-[#1e3a2a]">
                  {r}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelBase} htmlFor="active_positions">
              Active MCA Positions
            </label>
            <select
              id="active_positions"
              data-testid="select-positions"
              required
              value={form.active_positions}
              onChange={(e) => update("active_positions", e.target.value)}
              className={`${fieldBase} ${
                !form.active_positions ? "text-[#a8ab9f]" : ""
              }`}
            >
              <option value="" disabled>
                Select
              </option>
              {POSITION_OPTIONS.map((p) => (
                <option key={p} value={p} className="text-[#1e3a2a]">
                  {p}
                </option>
              ))}
            </select>
          </div>

          <label className="flex cursor-pointer items-start gap-3 pt-1">
            <input
              data-testid="input-consent"
              type="checkbox"
              checked={form.consent}
              onChange={(e) => update("consent", e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-[#223f2e]"
            />
            <span className="text-[12.5px] leading-relaxed text-[#8a8f85]">
              I agree to be contacted by {" "}
              {"Guardian Advisory Group"} by phone, text, or email regarding my
              inquiry. See our{" "}
              <Link to="/privacy" className="underline hover:text-[#566257]">
                Privacy Policy
              </Link>
              . This is not a loan offer and does not guarantee a specific
              outcome.
            </span>
          </label>

          {error && (
            <p data-testid="form-error" className="text-[13px] font-medium text-[#a55a38]">
              {error}
            </p>
          )}

          <button
            data-testid="assessment-submit"
            type="submit"
            disabled={status === "submitting"}
            className="group flex w-full items-center justify-center gap-2 rounded-full bg-[#bf9a4d] px-7 py-4 text-[15px] font-semibold text-[#1c3325] shadow-[0_10px_30px_-12px_rgba(191,154,77,0.9)] transition-[background-color,transform] duration-300 hover:bg-[#a9853c] hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {status === "submitting" ? "Submitting…" : "Request My Free Assessment"}
            {status !== "submitting" && (
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            )}
          </button>
        </form>
      )}
    </div>
  );
};

const Hero = () => (
  <section id="top" className="relative overflow-hidden">
    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 pb-24 pt-14 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16 lg:px-10 lg:pt-20">
      <div className="max-w-xl">
        <Reveal>
          <span className="inline-flex items-center gap-2 rounded-full bg-[#dde4db] px-4 py-1.5 text-[13px] font-medium text-[#2f5540]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#3e7a52]" />
            For business owners with MCA debt
          </span>
        </Reveal>
        <Reveal i={1}>
          <h1 className="mt-6 pb-1 font-display text-[46px] font-semibold leading-[1.06] tracking-[-0.01em] text-[#1e3a2a] sm:text-[58px] lg:text-[66px]">
            Stop Juggling Payments You Can't Keep Up With
          </h1>
        </Reveal>
        <Reveal i={2}>
          <p className="mt-6 max-w-lg text-[17px] leading-relaxed text-[#4b5a4f]">
            Guardian Advisory Group helps business owners consolidate multiple
            merchant cash advances into one manageable payment — negotiated
            directly with your lenders.
          </p>
        </Reveal>
        <Reveal i={3}>
          <div className="mt-8">
            <GoldButton
              testid="hero-cta"
              onClick={scrollToForm}
              className="px-8 py-4 text-base"
            >
              Get Your Free Assessment
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </GoldButton>
          </div>
        </Reveal>
        <Reveal i={4}>
          <ul className="mt-8 grid gap-x-8 gap-y-3 text-[15px] font-medium text-[#25402f] sm:grid-cols-2">
            {[
              "Confidential, no obligation",
              "Response within 24 hours",
              "We negotiate directly with lenders",
            ].map((t) => (
              <li key={t} className="flex items-center gap-2.5">
                <Check className="h-4 w-4 text-[#3e7a52]" strokeWidth={3} />
                {t}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal i={5}>
          <div className="mt-12">
            <HeroPhotos />
          </div>
        </Reveal>
      </div>

      <Reveal i={2} className="lg:pt-2">
        <AssessmentForm />
      </Reveal>
    </div>
  </section>
);

const stackItems = [
  {
    n: "01",
    title: "Daily or weekly withdrawals",
    body: "Multiple lenders pulling from the same account, often before you've covered payroll or rent.",
  },
  {
    n: "02",
    title: "Cash flow that never resets",
    body: "Revenue comes in, but it's gone before it can be reinvested in the business.",
  },
  {
    n: "03",
    title: "Constant lender calls",
    body: "Every position added makes the next conversation with funders harder to have.",
  },
];

const StackingCycle = () => (
  <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-24">
    <Reveal>
      <p className="eyebrow">The stacking cycle</p>
      <h2 className="mt-3 font-display text-[34px] font-semibold leading-tight text-[#1e3a2a] sm:text-[44px]">
        When one advance turns into four
      </h2>
      <p className="mt-5 max-w-2xl text-[17px] leading-relaxed text-[#4b5a4f]">
        Most business owners we work with didn't plan to end up here. It usually
        starts with one advance to cover a gap — then another to cover the first
        one's payments.
      </p>
    </Reveal>

    <div className="mt-12 grid gap-6 md:grid-cols-3">
      {stackItems.map((item, i) => (
        <Reveal key={item.n} i={i}>
          <div className="h-full rounded-2xl border border-[#e7e2d4] bg-white/70 p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_60px_-40px_rgba(34,63,46,0.4)]">
            <span className="font-display text-[40px] font-medium text-[#c48b7c]">
              {item.n}
            </span>
            <h3 className="mt-4 text-[19px] font-bold text-[#1e3a2a]">
              {item.title}
            </h3>
            <p className="mt-3 text-[15px] leading-relaxed text-[#566257]">
              {item.body}
            </p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const steps = [
  {
    n: 1,
    title: "Free assessment",
    body: "We review your current MCA positions, balances, and payment schedule — no cost, no obligation.",
  },
  {
    n: 2,
    title: "Direct negotiation",
    body: "We work directly with your funders to explore consolidation and payment restructuring options.",
  },
  {
    n: 3,
    title: "One manageable payment",
    body: "Where possible, your positions are consolidated into a single structure sized to your cash flow.",
  },
];

const HowItWorks = () => (
  <section className="bg-[#dde4db]">
    <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-24">
      <Reveal>
        <p className="eyebrow">How it works</p>
        <h2 className="mt-3 font-display text-[34px] font-semibold leading-tight text-[#1e3a2a] sm:text-[44px]">
          Three steps toward one payment
        </h2>
      </Reveal>

      <div className="relative mt-14 grid gap-12 md:grid-cols-3 md:gap-8">
        {steps.map((s, i) => (
          <Reveal key={s.n} i={i}>
            <div className="relative">
              <div className="flex items-center">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#223f2e] font-display text-[19px] font-semibold text-[#f4f1e9]">
                  {s.n}
                </div>
                {i < steps.length - 1 && (
                  <div className="ml-4 hidden h-px flex-1 border-t border-dashed border-[#9db4a2] md:block" />
                )}
              </div>
              <h3 className="mt-6 text-[19px] font-bold text-[#1e3a2a]">
                {s.title}
              </h3>
              <p className="mt-3 max-w-xs text-[15px] leading-relaxed text-[#4b5a4f]">
                {s.body}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const benefits = [
  {
    icon: Shield,
    title: "Confidential process",
    body: "Your information stays between you and your Guardian Advisory contact.",
  },
  {
    icon: Users,
    title: "Direct lender relationships",
    body: "We negotiate with your funders directly, so you don't have to.",
  },
  {
    icon: Clock,
    title: "Fast initial response",
    body: "Most inquiries get a callback from a specialist within 24 hours.",
  },
  {
    icon: CheckCircle2,
    title: "Clear next steps",
    body: "You'll know exactly what your options are before you commit to anything.",
  },
];

const WhyGuardian = () => (
  <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-24">
    <Reveal>
      <p className="eyebrow">Why Guardian Advisory</p>
      <h2 className="mt-3 font-display text-[34px] font-semibold leading-tight text-[#1e3a2a] sm:text-[44px]">
        Built for business owners, not lenders
      </h2>
    </Reveal>

    <div className="mt-12 grid gap-6 md:grid-cols-2">
      {benefits.map((b, i) => (
        <Reveal key={b.title} i={i}>
          <div className="flex h-full items-start gap-4 rounded-2xl border border-[#e7e2d4] bg-white/70 p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_60px_-40px_rgba(34,63,46,0.4)]">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#e7efe6]">
              <b.icon className="h-5 w-5 text-[#2f5540]" strokeWidth={2} />
            </div>
            <div>
              <h3 className="text-[18px] font-bold text-[#1e3a2a]">
                {b.title}
              </h3>
              <p className="mt-2 text-[15px] leading-relaxed text-[#566257]">
                {b.body}
              </p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const testimonials = [
  {
    quote:
      "Guardian Advisory gave us breathing room when we needed it most. Our daily MCA payments were crushing our cash flow, and within weeks they helped us negotiate a manageable payment plan. Instead of worrying about withdrawals every day, we're focused on growing our business again. I highly recommend their team.",
    name: "Michael R.",
    detail: "Construction Company Owner",
  },
  {
    quote:
      "I spoke with several companies before choosing Guardian Advisory, and they were the only ones that took the time to explain every option without making unrealistic promises. They reduced our monthly payment significantly and kept us informed throughout the process. It was one of the best business decisions we've made.",
    name: "Sarah L.",
    detail: "Retail Business Owner",
  },
  {
    quote:
      "After taking on multiple merchant cash advances, we felt trapped. Guardian Advisory created a strategy that consolidated our payments and negotiated with our lenders, giving us predictable payments we could actually afford. Our cash flow has improved dramatically, and we finally have a path forward.",
    name: "David P.",
    detail: "Transportation Company Owner",
  },
];

const Testimonials = () => (
  <section className="bg-[#dde4db]">
    <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-24">
      <Reveal>
        <p className="eyebrow">Client experiences</p>
        <h2 className="mt-3 font-display text-[34px] font-semibold leading-tight text-[#1e3a2a] sm:text-[46px]">
          What business owners say
        </h2>
      </Reveal>

      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {testimonials.map((t, i) => (
          <Reveal key={i} i={i}>
            <div className="flex h-full flex-col rounded-2xl bg-white p-8 shadow-[0_30px_60px_-45px_rgba(34,63,46,0.5)]">
              <Quote className="h-7 w-7 fill-[#bf9a4d] text-[#bf9a4d]" />
              <p className="mt-5 flex-1 text-[16px] italic leading-relaxed text-[#3a463d]">
                {t.quote}
              </p>
              <div className="mt-6">
                <p className="text-[15px] font-bold text-[#1e3a2a]">
                  {t.name}
                </p>
                <p className="text-[14px] text-[#566257]">{t.detail}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const faqs = [
  {
    q: "What is MCA debt consolidation?",
    a: "It's the process of combining multiple merchant cash advance obligations into a single, more manageable payment structure — typically through direct negotiation with your funders. The goal is to reduce the strain of daily or weekly withdrawals so more revenue stays in your business.",
  },
  {
    q: "Is MCA consolidation legal?",
    a: "Yes. Consolidating or restructuring merchant cash advance obligations through direct negotiation with your funders is a legitimate process. We recommend reviewing any agreement with a qualified attorney or advisor before signing.",
  },
  {
    q: "How does the process work?",
    a: "It starts with a free assessment of your current positions, balances, and payment schedule. From there, we work directly with your funders to explore consolidation and restructuring options, and — where possible — help combine your positions into a single structure sized to your cash flow.",
  },
  {
    q: "What are the eligibility requirements?",
    a: "Every situation is different. Generally, we work with business owners who have one or more active merchant cash advances and are finding the payments difficult to manage. The best way to know your options is to complete a free, no-obligation assessment.",
  },
  {
    q: "Will this affect my personal credit?",
    a: "Merchant cash advances are business financing products and are handled differently from personal credit lines. Every situation is unique, so during your free assessment we'll walk through how any changes could relate to your specific accounts.",
  },
  {
    q: "Can I keep operating my business during the process?",
    a: "Yes. Our goal is to help ease the pressure on your cash flow so you can keep running and growing your business. We work in the background with your funders while you stay focused on operations.",
  },
  {
    q: "Will I need to provide collateral?",
    a: "This depends entirely on your specific situation and your funders. We are not a lender and do not issue loans, so there is no standard collateral requirement from us. We'll explain anything relevant to your circumstances before you commit to anything.",
  },
  {
    q: "How fast can payments actually change?",
    a: "Timelines depend on your number of positions and how quickly your funders respond. Many business owners hear back from a specialist within 24 hours, and we move as fast as your lenders allow from there.",
  },
  {
    q: "What does an assessment cost?",
    a: "The initial assessment is free and comes with no obligation. If we move forward together, we'll clearly explain any terms before you commit to anything.",
  },
  {
    q: "Do you guarantee a specific outcome?",
    a: "No. This is not a loan offer and no specific result can be guaranteed. What we can do is review your positions honestly and explain the realistic options available to you.",
  },
];

const FAQItem = ({ item, isOpen, onToggle, index }) => (
  <div className="border-b border-[#dcd6c6]">
    <button
      data-testid={`faq-toggle-${index}`}
      onClick={onToggle}
      className="flex w-full items-center justify-between gap-6 py-6 text-left"
    >
      <span className="text-[19px] font-bold text-[#1e3a2a]">{item.q}</span>
      {isOpen ? (
        <Minus className="h-5 w-5 shrink-0 text-[#3e7a52]" />
      ) : (
        <Plus className="h-5 w-5 shrink-0 text-[#3e7a52]" />
      )}
    </button>
    <div
      className={`grid transition-all duration-300 ease-out ${
        isOpen ? "grid-rows-[1fr] pb-6" : "grid-rows-[0fr]"
      }`}
    >
      <div className="overflow-hidden">
        <p className="max-w-3xl text-[16px] leading-relaxed text-[#4b5a4f]">
          {item.a}
        </p>
      </div>
    </div>
  </div>
);

const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="mx-auto max-w-4xl px-6 py-20 lg:py-24">
      <Reveal>
        <p className="eyebrow">Common questions</p>
        <h2 className="mt-3 font-display text-[34px] font-semibold leading-tight text-[#1e3a2a] sm:text-[46px]">
          Before you reach out
        </h2>
      </Reveal>
      <div className="mt-10">
        {faqs.map((item, i) => (
          <FAQItem
            key={i}
            item={item}
            index={i}
            isOpen={open === i}
            onToggle={() => setOpen(open === i ? -1 : i)}
          />
        ))}
      </div>
    </section>
  );
};

const MIN_DEBT = 20000;
const MAX_DEBT = 500000;
const SAVINGS_RATE = 0.25;
const fmtUSD = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);

const SavingsCalculator = () => {
  const [debt, setDebt] = useState(MIN_DEBT);
  const pct = ((debt - MIN_DEBT) / (MAX_DEBT - MIN_DEBT)) * 100;
  const savings = Math.round((debt * SAVINGS_RATE) / 100) * 100;
  const debtLabel = debt >= MAX_DEBT ? "$500,000+" : fmtUSD(debt);
  const savingsLabel = debt >= MAX_DEBT ? `${fmtUSD(savings)}+` : fmtUSD(savings);

  return (
    <section id="savings" className="mx-auto max-w-7xl scroll-mt-24 px-6 py-20 lg:px-10 lg:py-24">
      <Reveal className="text-center">
        <p className="eyebrow">Savings calculator</p>
        <h2 className="mx-auto mt-3 max-w-2xl font-display text-[34px] font-semibold leading-tight text-[#1e3a2a] sm:text-[46px]">
          See what you could be saving
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-[17px] leading-relaxed text-[#4b5a4f]">
          Lower your payments, free up cash flow, and unlock the potential to
          grow your business. Slide to your approximate MCA balance for an
          estimate.
        </p>
      </Reveal>

      <Reveal i={1}>
        <div
          data-testid="savings-calculator"
          className="mx-auto mt-12 max-w-4xl rounded-[26px] border border-[#ece7d9] bg-white p-6 shadow-[0_40px_80px_-45px_rgba(34,63,46,0.35)] sm:p-10"
        >
          <div className="grid items-center gap-8 md:grid-cols-[1.15fr_0.85fr]">
            <div>
              <h3 className="text-center text-[20px] font-bold text-[#1e3a2a] md:text-left">
                Choose your debt amount
              </h3>
              <p
                data-testid="calc-debt-value"
                className="mt-5 text-center font-display text-[42px] font-semibold text-[#1e3a2a] md:text-left"
              >
                {debtLabel}
              </p>
              <input
                data-testid="calc-slider"
                type="range"
                min={MIN_DEBT}
                max={MAX_DEBT}
                step={5000}
                value={debt}
                onChange={(e) => setDebt(Number(e.target.value))}
                className="savings-slider mt-6 w-full"
                style={{
                  background: `linear-gradient(to right, #223f2e 0%, #223f2e ${pct}%, #ddd6c6 ${pct}%, #ddd6c6 100%)`,
                }}
                aria-label="Debt amount"
              />
              <div className="mt-3 flex justify-between text-[13px] font-medium text-[#8a8f85]">
                <span>$20,000</span>
                <span>$500,000+</span>
              </div>
            </div>

            <div className="flex flex-col items-center justify-center rounded-2xl bg-[#1c3325] px-6 py-10 text-center">
              <p className="font-mono-label text-[11px] text-[#9fb2a3]">
                Your estimated savings
              </p>
              <p
                data-testid="calc-savings-value"
                className="mt-3 font-display text-[46px] font-semibold leading-none text-[#bf9a4d] sm:text-[54px]"
              >
                {savingsLabel}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col items-center">
          <GoldButton
            testid="calc-cta"
            onClick={scrollToForm}
            className="px-8 py-4 text-base"
          >
            Get Started
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </GoldButton>
          <p className="mt-6 max-w-2xl text-center text-[12.5px] leading-relaxed text-[#8a8f85]">
            Estimate for illustration only. Actual savings depend on your
            positions, balances, and your funders&rsquo; decisions. This is not
            a loan offer and no specific outcome is guaranteed.
          </p>
        </div>
      </Reveal>
    </section>
  );
};

const INDUSTRIES = [
  {
    name: "Trucking",
    img: "https://images.unsplash.com/photo-1720811559395-3ed8d1b16649?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    body: "Keep your trucks moving and your cash flow strong. We help owner-operators and fleets explore restructuring their MCA payments, so fuel, maintenance, and payroll come first instead of daily paybacks.",
  },
  {
    name: "Restaurants",
    img: "https://images.unsplash.com/photo-1667826876695-968bb16ff396?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    body: "Thin margins and seasonal swings make daily MCA withdrawals brutal. We work to consolidate stacked positions into a single payment that fits the real rhythm of your restaurant's revenue.",
  },
  {
    name: "Medical",
    img: "https://images.unsplash.com/photo-1629909613654-28e377c37b09?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    body: "Practices and clinics carrying MCA debt can free up capital for staff, equipment, and patient care by restructuring multiple advances into one manageable payment.",
  },
  {
    name: "Construction",
    img: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    body: "Between draw schedules and material costs, timing is everything. We help contractors smooth out cash flow between projects by restructuring their MCA obligations.",
  },
  {
    name: "Retail",
    img: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    body: "From boutiques to e-commerce, we help retailers consolidate stacked advances so more of every sale stays in the business instead of going straight to daily repayments.",
  },
];

const Industries = () => {
  const [active, setActive] = useState(0);
  const item = INDUSTRIES[active];
  const prev = () =>
    setActive((active - 1 + INDUSTRIES.length) % INDUSTRIES.length);
  const next = () => setActive((active + 1) % INDUSTRIES.length);

  return (
    <section id="industries" className="scroll-mt-24 bg-[#dde4db]">
      <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-24">
        <Reveal>
          <p className="eyebrow">Industries we serve</p>
          <h2 className="mt-3 max-w-2xl font-display text-[34px] font-semibold leading-tight text-[#1e3a2a] sm:text-[46px]">
            We work with businesses across industries
          </h2>
        </Reveal>

        <Reveal i={1}>
          <div className="mt-12 grid gap-8 lg:grid-cols-2 lg:items-center">
            <div className="overflow-hidden rounded-[26px] shadow-[0_40px_80px_-45px_rgba(34,63,46,0.5)]">
              <motion.img
                key={item.img}
                src={item.img}
                alt={item.name}
                initial={{ opacity: 0, scale: 1.02 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                className="h-[300px] w-full object-cover sm:h-[440px]"
              />
            </div>
            <div className="rounded-[26px] bg-white p-8 shadow-[0_40px_80px_-50px_rgba(34,63,46,0.4)] sm:p-10">
              <h3
                data-testid="industry-title"
                className="font-display text-[28px] font-semibold text-[#1e3a2a] sm:text-[32px]"
              >
                {item.name}
              </h3>
              <p className="mt-4 text-[16px] leading-relaxed text-[#4b5a4f]">
                {item.body}
              </p>
              <GoldButton
                testid="industry-cta"
                onClick={scrollToForm}
                className="mt-8 px-7 py-3.5"
              >
                Get Started
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </GoldButton>
            </div>
          </div>
        </Reveal>

        <Reveal i={2}>
          <div className="mt-8 flex items-center gap-3">
            <button
              data-testid="industry-prev"
              onClick={prev}
              aria-label="Previous industry"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#223f2e] text-[#f4f1e9] transition-transform hover:-translate-y-0.5"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex flex-1 gap-2 overflow-x-auto py-1">
              {INDUSTRIES.map((ind, i) => (
                <button
                  key={ind.name}
                  data-testid={`industry-tab-${ind.name.toLowerCase()}`}
                  onClick={() => setActive(i)}
                  className={`whitespace-nowrap rounded-full px-5 py-2.5 text-[14px] font-semibold transition-colors duration-200 ${
                    active === i
                      ? "bg-[#223f2e] text-[#f4f1e9]"
                      : "text-[#25402f] hover:bg-white/60"
                  }`}
                >
                  {ind.name}
                </button>
              ))}
            </div>
            <button
              data-testid="industry-next"
              onClick={next}
              aria-label="Next industry"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#223f2e] text-[#f4f1e9] transition-transform hover:-translate-y-0.5"
            >
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const FinalCTA = () => (
  <section className="bg-[#223f2e]">
    <div className="mx-auto max-w-5xl px-6 py-20 text-center lg:py-24">
      <Reveal>
        <p className="font-mono-label text-[12px] text-[#bf9a4d]">
          The first step is free
        </p>
        <h2 className="mx-auto mt-4 max-w-2xl font-display text-[36px] font-semibold leading-tight text-[#f4f1e9] sm:text-[48px]">
          See what one manageable payment could look like
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-[17px] leading-relaxed text-[#c3d1c6]">
          Get a confidential, no-obligation review of your current MCA positions
          from a consolidation specialist.
        </p>
        <div className="mt-9">
          <GoldButton
            testid="final-cta"
            onClick={scrollToForm}
            className="px-8 py-4 text-base"
          >
            Get Your Free Assessment
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </GoldButton>
        </div>
      </Reveal>
    </div>
  </section>
);

const Footer = SiteFooter;

export default function LandingPage() {
  return (
    <div className="App min-h-screen bg-[#f4f1e9]">
      <Header />
      <main>
        <Hero />
        <StackingCycle />
        <HowItWorks />
        <SavingsCalculator />
        <Industries />
        <WhyGuardian />
        <Testimonials />
        <FAQ />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
